/**
 * `rating` → **Customize Rating column** — rebuilt 1:1 from the capture
 * (`discovery/ui-reference/editors/rating/dialog-settings.{png,json}`, 2026-09-10, 1440 × 900).
 *
 * Measured, in order: a 320 × 275 card at 16 px inset; the title *Rating column settings*
 * (288 × 24); the section label **Symbol** (47 × 21) over a 288 × 32 two-button group
 * *Stars* | *Hearts* (144 each); **Scale** (34 × 21) over a three-button group *3* | *4* | *5*
 * (96 / 95 / 97), the current one highlighted; **Color** (34 × 21) over a row of 24 px circles.
 *
 * Two deliberate departures from the photograph, both because the *store* is wider than the
 * capture's column happened to be:
 *
 *  - the scale offers **3 … 10**, not 3 / 4 / 5. `settings.limit` is documented as *"between 3
 *    and 10. Defaults to 5"* `[09§A.7 rating]` and `RATING_MIN_LIMIT` / `RATING_MAX_LIMIT`
 *    enforce it, so offering three of the eight would make five legal scales unreachable. The
 *    captured column's own value (5) is simply the one that was selected that day.
 *  - the colour row is nine swatches of monday's own palette; the capture's hexes were not
 *    legible at 320 px (q517).
 */
import { RATING_MAX_LIMIT, RATING_MIN_LIMIT, ratingLimit, type RatingSettings } from '@elizmonday/shared';
import {
  ColorSwatches,
  HideSummaryField,
  SegmentedField,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

/** `settings.symbol` ∈ `stars` | `hearts` `[09§A.7 rating]`; the glyphs are the cell's. */
const SYMBOLS = [
  { id: 'stars', label: 'Stars', value: 'stars' },
  { id: 'hearts', label: 'Hearts', value: 'hearts' },
] as const;

const SCALES = Array.from({ length: RATING_MAX_LIMIT - RATING_MIN_LIMIT + 1 }, (_, index) => {
  const limit = RATING_MIN_LIMIT + index;
  return { id: String(limit), label: String(limit), value: limit };
});

export function RatingSettingsDialog({ settings, onCommit, onCancel }: ColumnSettingsProps<RatingSettings>) {
  const [draft, patch] = useSettingsDraft<RatingSettings>(settings);
  return (
    <SettingsForm
      title="Rating column settings"
      testId="rating-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SegmentedField
        label="Symbol"
        testId="rating-symbol"
        value={draft.symbol ?? 'stars'}
        options={SYMBOLS}
        onChange={(next) => patch({ symbol: next })}
      />
      <SegmentedField
        label="Scale"
        testId="rating-scale"
        value={ratingLimit(draft)}
        options={SCALES}
        onChange={(next) => patch({ limit: next })}
      />
      <ColorSwatches
        testId="rating-color"
        value={draft.color}
        onChange={(hex) => patch({ color: hex })}
      />
      <HideSummaryField
        testId="rating-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
